import React, {useEffect, useRef, useState} from 'react';
import {BaseInputProps} from '../..';

import {Tooltip} from '@mantine/core';
import {ErrorOption, useController, useFormContext} from 'react-hook-form';
import {RiInformationFill} from 'react-icons/ri';
import Loading from 'shared/migration-components/loading';
import {NestedKeys} from 'shared/types/key-manipulation';
import {SelectDataProps} from 'shared/types/select-data';
import getSentenceGender from 'shared/util/find-sentence-gender';
import {Container, ContainerLabel, Label, StyledSelect} from './styles';
import ErrorWrapper from '../error-wrapper';

export interface SelectProps<T = any> extends BaseInputProps<T> {
  onClick?: () => void;
  onChange?: (data?: any) => {key: string; value: any};
  value?: string;
  placeholder?: string;
  gridColumn?: number;
  data: SelectDataProps[];
  name: NestedKeys<T>;
  isLoading?: boolean;
  listedOnly?: boolean;
  creatable?: boolean;
}

const Select: React.FC<SelectProps> = ({
  name,
  label,
  onClick,
  placeholder,
  gridColumn,
  data,
  border,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hidden,
  disabled,
  isLoading,
  listedOnly = false,
  information,
  creatable,
  required,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const {control, setValue, formState, clearErrors} = useFormContext();
  const {field} = useController({control, name});
  const [query, setQuery] = useState('');
  const [createdData, setCreatedData] = useState<SelectDataProps>();
  const ref = useRef<HTMLInputElement>(null);

  const error: ErrorOption | undefined = formState.errors
    ? formState.errors[name as string]
    : undefined;

  const onChange = (value: string | null) => {
    clearErrors(name);
    const isData = data.find((item) => item.value === value)?.data;

    if (props.onChange && isData) {
      const {key, value} = props.onChange(isData);
      setValue(key, value);
    }
    field.onChange(value);
  };

  useEffect(() => {
    const isData = data?.find((item) => item.value === field.value);
    if (!!field.value && listedOnly && !isData) field.onChange(null);
  }, [data]);

  const onCreate = () => {
    if (!creatable) return;

    const item = {value: query, label: query};
    setCreatedData(item);
    field.onChange(query);
    setQuery('');
  };

  useEffect(() => {
    ref.current?.setAttribute('autocomplete', 'nope');
  }, []);

  return (
    <ErrorWrapper {...{error: error?.message, gridColumn}}>
      <Container {...{onClick, gridColumn, focused, border, error}}>
        <ContainerLabel>
          {label && (
            <Label {...{error: !!error}}>
              {label}
              {required && ' *'}
            </Label>
          )}
          {information && (
            <Tooltip
              label={information}
              multiline
              withArrow
              arrowSize={10}
              openDelay={500}
              w={200}
              position="top">
              <div>
                <RiInformationFill size={20} />
              </div>
            </Tooltip>
          )}
        </ContainerLabel>
        <StyledSelect
          searchable
          comboboxProps={{zIndex: 5000}}
          {...{placeholder, ref, error: !!error}}
          onSearchChange={setQuery}
          searchValue={query}
          data={createdData ? [...data, createdData] : data}
          onKeyDown={(event) => event.key === 'Enter' && query && onCreate()}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          value={field.value}
          onChange={onChange}
          disabled={disabled}
          rightSection={isLoading && <Loading />}
          style={{dropdown: {border: 'none', backgroundColor: 'red'}}}
          nothingFoundMessage={
            <div>
              {!isLoading && (
                <div
                  style={{
                    cursor: 'pointer',
                    color: 'black',
                    alignSelf: 'flex-start',
                  }}
                  onClick={onCreate}>
                  {creatable
                    ? `Adicionar ${query}`
                    : `Nenhum(a) ${label?.toLocaleLowerCase()} encontrad${
                        label ? getSentenceGender(label) : 'o'
                      }`}
                </div>
              )}
              {isLoading && <Loading />}
            </div>
          }
        />
      </Container>
    </ErrorWrapper>
  );
};

export default Select;
